import type { PlayerDetailsAdminField } from '../types'

/**
 * Admin-only form field → API key on GET/PUT /players/:id.
 *
 * These are the API's own keys (the `player` column names every player
 * endpoint uses), confirmed by the backend. The PUT rejects a key it does not
 * know with a 422, so a wrong key here shows up as a field error, not as a
 * save that silently writes nothing. Reads, the PUT payload and the error
 * mapping all derive from this one map.
 */
export const ADMIN_FIELD_TO_BE_KEY: Record<PlayerDetailsAdminField, string> = {
  previousTeam: 'previous_team',
  previousDivision: 'previous_division',
  preferredPosition: 'preferred_position',
  skillLevel: 'skill_level',
  jerseySize: 'jersey_size',
  shortSize: 'short_size',
  medicalConditions: 'medical_conditions',
  previousExperience: 'previous_experience',
  previousYearsPlayed: 'previous_years_played',
  howDidYouHear: 'how_did_you_hear_about_us',
  teamPreference: 'team_preference',
  teammateRequest: 'friend_teammate_request',
  additionalComments: 'additional_comments_notes',
  emailConsent: 'email_consent',
  isFlagged: 'flag',
  goalieFee: 'goalie_fee',
  highestLevelOfPlay: 'highest_level_of_organized_play',
  isFreeAgent: 'is_free_agent',
  isBlacklisted: 'blacklisted',
  blacklistComments: 'blacklist_comments',
  blacklistedAt: 'blacklisted_date',
  notes: 'notes',
  registrationNotes: 'registration_notes',
}

/** Admin fields held as booleans (switches); every other admin field is a string. */
export const ADMIN_BOOLEAN_FIELDS: ReadonlySet<PlayerDetailsAdminField> =
  new Set<PlayerDetailsAdminField>([
    'emailConsent',
    'isFlagged',
    'isFreeAgent',
    'isBlacklisted',
  ])

/** Admin fields the API sets itself. Read for display, never sent. */
export const ADMIN_READ_ONLY_FIELDS: ReadonlySet<PlayerDetailsAdminField> =
  new Set<PlayerDetailsAdminField>([
    'blacklistedAt',
    'previousYearsPlayed',
    'registrationNotes',
  ])

/** API `invalid_fields` key → admin form field, for inline error mapping. */
export const ADMIN_BE_TO_FORM_FIELD: Record<string, PlayerDetailsAdminField> =
  Object.fromEntries(
    (
      Object.entries(ADMIN_FIELD_TO_BE_KEY) as Array<
        [PlayerDetailsAdminField, string]
      >
    ).map(([formKey, beKey]) => [beKey, formKey])
  )

/** API `invalid_fields` key → human title, for the summary toast fallback. */
export const ADMIN_BE_TO_FIELD_TITLE: Record<string, string> = {
  previous_team: 'Previous Team',
  previous_division: 'Previous Division',
  preferred_position: 'Preferred Position',
  skill_level: 'Skill Level',
  jersey_size: 'Jersey Size',
  short_size: 'Short Size',
  medical_conditions: 'Medical Conditions',
  previous_experience: 'Previous Experience',
  how_did_you_hear_about_us: 'How Did You Hear About Us',
  team_preference: 'Team Preference',
  friend_teammate_request: 'Friend / Teammate Request',
  additional_comments_notes: 'Additional Comments / Notes',
  email_consent: 'Email Consent',
  flag: 'Flag',
  goalie_fee: 'Goalie Fee',
  highest_level_of_organized_play: 'Highest Level Of Organized Play',
  is_free_agent: 'Is Free Agent',
  blacklisted: 'Blacklisted',
  blacklist_comments: 'Blacklist Comments',
  notes: 'Notes',
}
